"use client"

import Link from "next/link"
import { ArrowRight, Mail, MapPin, Phone } from "lucide-react"
import { motion } from "motion/react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"

const ease = [0.25, 0.4, 0, 1] as const

export function CtaBanner() {
  return (
    <section className="section-normal px-4 md:px-6">
      <div className="mx-auto max-w-7xl">
        <motion.div
          className="relative overflow-hidden rounded-3xl bg-primary px-6 py-12 text-primary-foreground md:px-12 md:py-16"
          initial={{ opacity: 0, y: 32, scale: 0.98 }}
          whileInView={{ opacity: 1, y: 0, scale: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
        >
          {/* Subtle dot pattern */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[length:20px_20px] pointer-events-none" />

          <div className="relative flex flex-col items-start gap-8 md:flex-row md:items-center md:justify-between">
            <div>
              <h2
                className="text-3xl font-black tracking-tight md:text-4xl lg:text-5xl"
                style={{ textWrap: "balance", letterSpacing: "-0.02em" }}
              >
                {"À votre écoute pour tous vos besoins"}
              </h2>
              <p className="mt-3 max-w-[50ch] text-base text-primary-foreground/80 md:text-lg">
                {"Nous mettons notre réactivité et notre compétitivité à votre service."}
              </p>
              <div className="mt-4 flex flex-col gap-2 text-sm text-primary-foreground/70">
                <div className="flex items-center gap-2">
                  <MapPin className="size-4 shrink-0" />
                  <span>ZA Les Bosquets N°2, 15 Chemin Des Bœufs, 95540 MÉRY SUR OISE</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="size-4 shrink-0" />
                  <a href="tel:+33130370530" className="tabular-nums underline underline-offset-2 decoration-primary-foreground/30 transition-[color,text-decoration-color] duration-200 hover:text-primary-foreground hover:decoration-primary-foreground/60">+33 (0)1 30 37 05 30</a>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="size-4 shrink-0" />
                  <a href="mailto:contact@adis95.fr" className="underline underline-offset-2 decoration-primary-foreground/30 transition-[color,text-decoration-color] duration-200 hover:text-primary-foreground hover:decoration-primary-foreground/60">contact@adis95.fr</a>
                </div>
              </div>
            </div>

            {/*
             * Two-button CTA stack: primary "Appeler" pill (phone is
             * the highest-intent action for B2B industrial buyers per
             * the CRO audit) + secondary "Devis" pill. Previously the
             * phone number was small underlined body text inside the
             * info column above; now both actions get button-level
             * affordance with the call sitting on top.
             */}
            <div className="flex shrink-0 flex-col gap-2.5 sm:flex-row md:flex-col">
              <Button
                asChild
                size="lg"
                className={cn(
                  "group h-12 gap-3 rounded-full pl-6 pr-2",
                  "bg-white text-primary font-medium",
                  "hover:bg-white/90 active:scale-[0.97]",
                  "shadow-lg shadow-primary/20",
                )}
              >
                <a href="tel:+33130370530" aria-label="Appeler le 01 30 37 05 30">
                  <Phone className="size-4" aria-hidden="true" />
                  <span className="tabular-nums">01 30 37 05 30</span>
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary/10 transition-[transform] duration-200 group-hover:translate-x-0.5">
                    <ArrowRight className="size-4 text-primary" />
                  </span>
                </a>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className={cn(
                  "group h-12 gap-3 rounded-full pl-6 pr-2",
                  "border-primary-foreground/40 bg-transparent text-primary-foreground font-medium",
                  "hover:bg-primary-foreground/10 active:scale-[0.97]",
                )}
              >
                <Link href="/contact">
                  <span>Nous contacter</span>
                  <span className="flex size-8 items-center justify-center rounded-full bg-primary-foreground/15 transition-[transform] duration-200 group-hover:translate-x-0.5">
                    <ArrowRight className="size-4 text-primary-foreground" />
                  </span>
              </Link>
            </Button>
          </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
